import { boliviaDateStamp, DashboardPayment, DashboardPaymentOrigin } from './dashboardFinancials';

export type DashboardPeriod = 'today' | 'week' | 'month' | 'custom';
export interface CollectionDay {
  date: string;
  label: string;
  reserva: number;
  cafeteria: number;
  sesion: number;
  administrativo: number;
  total: number;
}

const dateValue = (value: string): number => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value ? date.getTime() : NaN;
};
const dateKey = (date: Date): string => date.toISOString().slice(0, 10);

/** Calendar boundaries use Bolivia's date, independently of the browser's timezone. */
export const dashboardPeriodRange = (period: Exclude<DashboardPeriod, 'custom'>, today = boliviaDateStamp()) => {
  const date = new Date(dateValue(today));
  if (Number.isNaN(date.getTime())) throw new Error('Fecha inválida.');
  if (period === 'week') date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
  if (period === 'month') date.setUTCDate(1);
  return { start: dateKey(date), end: today };
};

/** Every payment contributes once to its origin; zero days fill the selected calendar range. */
export const dashboardCollections = (payments: DashboardPayment[], start: string, end: string) => {
  const from = dateValue(start), until = dateValue(end);
  if (!Number.isFinite(from) || !Number.isFinite(until) || from > until) return { rows: [] as CollectionDay[], monthly: false };
  const monthly = (until - from) / 86400000 >= 62;
  const buckets = new Map<string, Record<DashboardPaymentOrigin, number>>();
  const cursor = new Date(from);
  if (monthly) cursor.setUTCDate(1);
  while (cursor.getTime() <= until) {
    const key = monthly ? dateKey(cursor).slice(0, 7) : dateKey(cursor);
    buckets.set(key, { reserva: 0, cafeteria: 0, sesion: 0, administrativo: 0 });
    if (monthly) cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    else cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  payments.forEach(payment => {
    if (payment.day < start || payment.day > end) return;
    const bucket = buckets.get(monthly ? payment.day.slice(0, 7) : payment.day);
    if (!bucket) return;
    const [units, fraction = ''] = payment.amount.split('.');
    const cents = Number(units) * 100 + Number(fraction.padEnd(2, '0'));
    if (!Number.isSafeInteger(cents) || cents < 0 || !Number.isSafeInteger(bucket[payment.origin] + cents)) throw new Error('Respuesta financiera inválida.');
    bucket[payment.origin] += cents;
  });
  const rows = Array.from(buckets, ([date, cents]): CollectionDay => {
    const total = cents.reserva + cents.cafeteria + cents.sesion + cents.administrativo;
    if (!Number.isSafeInteger(total)) throw new Error('Respuesta financiera inválida.');
    return {
      date, label: monthly ? `${date.slice(5, 7)}/${date.slice(0, 4)}` : `${date.slice(8, 10)}/${date.slice(5, 7)}`,
      reserva: cents.reserva / 100, cafeteria: cents.cafeteria / 100,
      sesion: cents.sesion / 100, administrativo: cents.administrativo / 100, total: total / 100,
    };
  });
  return { rows, monthly };
};
